import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Checkbox, Radio, TextField, Tooltip, useMediaQuery, useTheme } from '@material-ui/core';

import { createApplicantAutomation } from '../../../applicant-automations/actions/create-applicant-automation';
import { createOrUpdateJobOverride } from '../../../applicant-automations/actions/create-or-update-job-override';
import { loadApplicantAutomations } from '../../../applicant-automations/actions/load-applicant-automations';
import { updateApplicantAutomation } from '../../../applicant-automations/actions/update-applicant-automation';
import { applicantAutomationsForOrgIdSelector } from '../../../applicant-automations/applicant-automation-selectors';
import { loadOrgJobs } from '../../../job/actions/load-org-jobs';

import { FeatureFlagged, FeatureGatedVariant } from '../../../feature-gates/feature-flagged';
import { FeatureGates } from '../../../feature-gates/feature-gates';
import { LegacyMessageTemplatePreview } from '../../../message-templates/components/legacy-message-template-preview';
import { MessageTemplatePreview } from '../../../message-templates/components/message-template-preview';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';
import { UiHideShowToggle } from '../../../ui/ui-hide-show-toggle';

import { JobOverridesDialog } from './job-overrides-dialog';

import { allPossibleStatuses } from '../../../config/application-status-config';
import { CkEditorNewlineTrimmer } from '../../../util/ckeditor-newline-trimmer';
import { LanguageCode } from '../../../util/languages';
import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { MESSAGE_TYPE, MessageType } from '../../../util/message-types';
import { confirm } from '../../../widgets/confirmation-dialog';
import { LegacyMultiLanguageMessageComposer } from '../../../widgets/message-composer/legacy-multiple-language-message-composer';
import { MultiLanguageMessageComposer } from '../../../widgets/message-composer/multiple-language-message-composer';
import {
  APPLICANT_AUTOMATION_COMMUNICATION_TYPES,
  CREATABLE_APPLICANT_AUTOMATION_TYPES,
} from '../../utils/applicant-automation-communication-types';
import {
  OffsetDirectionDropdown,
  TriggerDropdown,
} from '../../utils/applicant-automation-dialog-utils';
import {
  AutomationsByTriggerDictionary,
  ConfirmableMessageText,
  getAllOverridesForOrgDefault,
  getApplicantAutomationTriggerOptionsBySection,
  getDefaultTitleFromStatusAndCommunicationType,
} from '../../utils/applicant-automation-display-utils';
import { isExistingApplicantAutomation } from '../../utils/is-applicant-automation';

type InputProps = {
  editingForJob?: Job;
  parser: MessageTemplateParser;
  automation?: null | ApplicantAutomation | Partial<ApplicantAutomationCopy>;
  fallbackDescription?: string;
  section: ApplicantAutomationSection;
  showBulkUpdate?: boolean;
  orgId: number;
  orgName: string;
  parentAutomationsForSection?: AutomationsByTriggerDictionary;
  onClose?: () => void;
  addingAutomationForTrigger?: ApplicantAutomationCommunicationType;
  copiedFromAutomationId?: number;
};

export const ApplicantAutomationDialog = ({
  automation,
  parser,
  orgName,
  onClose,
  orgId,
  editingForJob,
  fallbackDescription,
  section,
  showBulkUpdate,
  parentAutomationsForSection,
  addingAutomationForTrigger,
  copiedFromAutomationId,
}: InputProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadOrgJobs(orgId));
    dispatch(loadApplicantAutomations({ orgId }));
  });

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const dateReminders = [
    APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTERVIEW_REMINDER,
    APPLICANT_AUTOMATION_COMMUNICATION_TYPES.FIRST_DAY_REMINDER,
  ];
  const isFutureDateReminder = Boolean(
    automation?.communicationType && dateReminders.includes(automation.communicationType),
  );

  const [willUpdateAllJobs, setWillUpdateAllJobs] = useState<boolean>(false);

  // description and message
  const [templateString, setTemplateString] = useState<string>(automation?.template || '');
  const [description, setDescription] = useState<string>(
    automation?.description || fallbackDescription || '',
  );
  const [isConfirmationRequired, setIsConfirmationRequired] = useState<boolean>(
    Boolean(automation?.isConfirmationRequired),
  );
  const [messageDeliveryType, setMessageDeliveryType] = useState<MessageType>(
    automation?.messageType || MESSAGE_TYPE.TEXT,
  );
  const [messageLoading, setMessageLoading] = useState(false);

  // timing
  // while future date reminders all use before, Interview reminders are the only one that can be before or after
  const [allowBeforeOrAfter, setAllowBeforeOrAfter] = useState<boolean>(
    automation?.communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTERVIEW_REMINDER,
  );
  // Send Immediately is easier to parse in the ui than sending zero hours before/after a trigger,
  // so we will show both an offset and immediate option to the user
  const [sendImmediately, setSendImmediately] = useState<boolean>(
    !isFutureDateReminder && !automation?.sendTimeOffsetHours,
  );
  const [sendTimeOffsetHours, setSendTimeOffsetHours] = useState<number | ''>(
    automation?.sendTimeOffsetHours ? Math.abs(automation.sendTimeOffsetHours) : 1,
  );
  const isDefaultOffsetBefore = automation?.sendTimeOffsetHours
    ? automation?.sendTimeOffsetHours < 0
    : isFutureDateReminder;
  const [offsetDirection, setOffsetDirection] = useState<string>(
    isDefaultOffsetBefore ? 'Before' : 'After',
  );
  const [isOnlySendDuringBusinessHours, setIsOnlySendDuringBusinessHours] = useState<boolean>(
    // Because we store booleans as tinyInt this could actually be 0 or 1 but checkboxes will not accept numbers as props
    automation?.isOnlySendDuringBusinessHours === undefined
      ? true
      : Boolean(automation?.isOnlySendDuringBusinessHours),
  );
  const [isFollowUp, setIsFollowUp] = useState<boolean>(!!automation?.isFollowUp);

  // communication type
  const [communicationType, setCommunicationType] =
    useState<ApplicantAutomationCommunicationType | null>(
      automation?.communicationType || addingAutomationForTrigger || null,
    );
  const [toStatus, setToStatus] = useState<ApplicationStatus | undefined>(
    automation?.toStatus || undefined,
  );

  // display
  const [displayAdvancedSettings, setDisplayAdvancedSettings] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<boolean>(false);
  const [jobOverrideDialogOpen, setJobOverrideDialogOpen] = useState<boolean>(false);

  const automationsForOrg = useSelector((state: any) =>
    applicantAutomationsForOrgIdSelector(state, orgId),
  );

  const handleToggleDisplayAdvancedSettings = () =>
    setDisplayAdvancedSettings(!displayAdvancedSettings);

  const handleToggleSendDuringBusinessHours = () =>
    setIsOnlySendDuringBusinessHours(!isOnlySendDuringBusinessHours);

  const handleToggleJobOverrideDialog = () => setJobOverrideDialogOpen(!jobOverrideDialogOpen);

  const handleMessageChange = (messageMap: Record<LanguageCode, string>) => {
    setTemplateString(messageMap?.en ?? templateString);
  };

  const handleMessageTypeChange = (type: MessageType) => {
    setMessageDeliveryType(type);
  };

  const handleToggleIsConfirmationRequired = () => {
    setIsConfirmationRequired(!isConfirmationRequired);
  };

  const toggleSendImmediately = () => {
    setSendImmediately(!sendImmediately);
  };

  const handleWillUpdateAllJobsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWillUpdateAllJobs(e.target.checked);
  };

  const handleToggleFollowUp = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFollowUp(e.target.checked);
  };

  const handleCommunicationTypeChange = ({ target }: React.ChangeEvent<{ value: unknown }>) => {
    const value = target.value as string;
    const acceptedCommunicationTypes = Object.values(
      CREATABLE_APPLICANT_AUTOMATION_TYPES,
    ) as string[];
    if (acceptedCommunicationTypes.includes(value)) {
      const isStatusChange = value.includes(APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE);
      // communicationType options are either in the form of ApplicantAutomationCommunicationType or STATUS_CHANGE_[toStatus]
      const statusChangeTrigger = value.replace(
        `${APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE}_`,
        '',
      ) as ApplicationStatus;
      const isValidStatus = allPossibleStatuses.includes(statusChangeTrigger);
      if (isStatusChange && isValidStatus) {
        setCommunicationType(APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE);
        setToStatus(statusChangeTrigger);
      } else {
        setCommunicationType(value as ApplicantAutomationCommunicationType);
        setToStatus(undefined);
      }
    }

    if (
      (value as ApplicantAutomationCommunicationType) ===
      APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTERVIEW_REMINDER
    ) {
      setAllowBeforeOrAfter(true);
    } else {
      setAllowBeforeOrAfter(false);
    }

    if (dateReminders.includes(value as ApplicantAutomationCommunicationType)) {
      setOffsetDirection('Before');
    } else {
      setOffsetDirection('After');
    }
  };

  const handleSendTimeOffsetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const offset = parseInt(e.target.value, 10);
    if (!Number.isNaN(offset) && offset > 0) {
      setSendTimeOffsetHours(Math.abs(offset));
    } else if (e.target.value === '') {
      setSendTimeOffsetHours(e.target.value);
    } else if (offset === 0) {
      // 0 hours is effectively sending immediately, so we should swap the toggle and
      // reset the offset hours to the default of 1 hour in case they switch back
      // sendTimeOffsetHours is ignored in the automation update if sendImmediately is true
      setSendImmediately(true);
      setSendTimeOffsetHours(1);
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleAutomationRecurrenceChange = ({ target }: React.ChangeEvent<{ value: unknown }>) => {
    setOffsetDirection(target.value as string);
  };

  const onPreviewError = () => {
    setPreviewError(true);
  };

  const onTemplateChange = () => {
    setPreviewError(false);
  };

  const handleMessageLoading = (loading: boolean) => {
    setMessageLoading(loading);
  };

  const submit = async () => {
    let cleanTemplate = templateString;
    if (messageDeliveryType === MESSAGE_TYPE.TEXT) {
      cleanTemplate = CkEditorNewlineTrimmer.trimMessage(templateString);
    }

    const sendOffset = sendImmediately
      ? 0
      : offsetDirection === 'Before' && sendTimeOffsetHours
      ? sendTimeOffsetHours * -1
      : sendTimeOffsetHours;

    const baseUpdate = {
      description,
      template: cleanTemplate,
      sendTimeOffsetHours: sendOffset || 0,
      isConfirmationRequired,
      isOnlySendDuringBusinessHours,
      willUpdateAllJobs,
      messageType: messageDeliveryType,
      ...(editingForJob ? { jobId: editingForJob.job_id } : { defaultForOrgId: orgId }),
    };

    if (willUpdateAllJobs) {
      const updateConfirmed = await confirm(`
        Are you sure you want to replace custom messages for ${jobOverrides.length} job${
        jobOverrides.length === 1 ? '' : 's'
      } to this updated default? This action cannot be undone.`);

      if (!updateConfirmed) {
        return;
      }
    }

    if (isExistingApplicantAutomation(automation) && editingForJob) {
      dispatch(createOrUpdateJobOverride(automation, baseUpdate));
    } else if (isExistingApplicantAutomation(automation)) {
      dispatch(updateApplicantAutomation(automation.applicantTriggeredCommunicationId, baseUpdate));
    } else {
      if (!communicationType) {
        onPreviewError();
        return;
      }

      const newAutomation = {
        ...baseUpdate,
        communicationType,
        toStatus,
        isFollowUp,
        isActive: true,
        copiedFromAutomationId,
      };

      dispatch(createApplicantAutomation(newAutomation));
    }

    if (onClose) {
      onClose();
    }
  };

  const messageForPreview = {
    template: templateString,
    isConfirmationRequired,
  };

  const titleAction = isExistingApplicantAutomation(automation) ? 'Edit' : 'Add';

  const title = `${titleAction} Applicant Automation${
    editingForJob ? ` For ${editingForJob.title}` : ''
  }`;

  const messageType = communicationType
    ? getDefaultTitleFromStatusAndCommunicationType(communicationType, toStatus).toLowerCase()
    : '';

  const jobOverrides = isExistingApplicantAutomation(automation)
    ? getAllOverridesForOrgDefault(automation, automationsForOrg)
    : [];
  const hasJobOverrides = !editingForJob && !!automation && !!jobOverrides.length;

  const communicationTypeValue =
    (communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE
      ? `${communicationType}_${toStatus}`
      : communicationType) || '';

  const hasParentAutomations = Boolean(
    communicationTypeValue && parentAutomationsForSection?.[communicationTypeValue]?.length,
  );

  const triggerLabel =
    getApplicantAutomationTriggerOptionsBySection(section).find(
      (option) => option.value === communicationTypeValue,
    )?.label || '';

  const disableSave =
    !communicationType ||
    (!sendImmediately && !sendTimeOffsetHours) ||
    !description ||
    !templateString ||
    previewError ||
    messageLoading;

  return (
    <UiDialog title={title} onClose={onClose} size="sm">
      <UiDialog.Content>
        <div className="typo-title u-margin-top u-margin-bottom-md u-flex-center">
          <span>Send Message:</span>
          <div className="u-margin-left-lg u-flexGrow-1">
            <TriggerDropdown
              disabled={!sendImmediately}
              section={section}
              automation={automation}
              addingAutomationForTrigger={addingAutomationForTrigger}
              communicationTypeValue={communicationTypeValue}
              handleCommunicationTypeChange={handleCommunicationTypeChange}
              triggerLabel={triggerLabel}
            />
          </div>
        </div>
        <div className="u-padding u-margin-bottom u-width-100 card">
          <div className="u-display-flex u-alignItems-center u-margin-bottom">
            <Radio color="primary" onClick={toggleSendImmediately} checked={sendImmediately} />
            <div className="typo-title u-display-flex u-alignItems-center">
              Immediately
              <div className="u-margin-left-sm typo-title">
                {triggerLabel ? `when ${triggerLabel.toLowerCase()}` : ''}
              </div>
            </div>
          </div>
          <div className="u-display-flex u-alignItems-center">
            <Radio color="primary" onClick={toggleSendImmediately} checked={!sendImmediately} />
            <div className="u-margin-left-sm">
              <TextField
                className="u-no-margin u-width-50px"
                variant="standard"
                margin="dense"
                value={sendTimeOffsetHours}
                onChange={handleSendTimeOffsetChange}
                disabled={sendImmediately}
              />
            </div>
            <div className="u-margin-left-sm typo-title">Hour(s)</div>
            <div className="u-margin-left-sm">
              <OffsetDirectionDropdown
                disabled={sendImmediately}
                allowBeforeOrAfter={allowBeforeOrAfter}
                offsetDirection={offsetDirection}
                handleAutomationRecurrenceChange={handleAutomationRecurrenceChange}
              />
            </div>
            <div className="u-margin-left-sm typo-title">{triggerLabel.toLowerCase()}</div>
          </div>
        </div>
        <UiHideShowToggle
          onChange={handleToggleDisplayAdvancedSettings}
          isOpen={displayAdvancedSettings}
          title="additional settings"
          className="u-margin-bottom-sm"
        />
        {displayAdvancedSettings ? (
          <div className="u-padding u-margin-bottom u-width-100 card">
            <div className="u-display-flex u-flex-directionColumn u-alignItems-center u-margin-y">
              <Checkbox
                className="u-no-padding"
                checked={isOnlySendDuringBusinessHours}
                onChange={handleToggleSendDuringBusinessHours}
              />
              <div className="u-margin-left-sm u-display-flex u-alignItems-center">
                Only send this message during business hours
                <Tooltip
                  title="Business hours are 8:00 am to 6:00 pm, local time"
                  placement="right"
                >
                  <span>
                    <MaterialDesignIcon
                      iconName={ICON_NAMES.HELP}
                      className="u-margin-left-lg"
                      size="sm"
                    />
                  </span>
                </Tooltip>
              </div>
            </div>
            {hasParentAutomations && !isExistingApplicantAutomation(automation) && (
              <div className="u-display-flex u-alignItems-flexStart u-margin-y">
                <Checkbox
                  className="u-no-padding"
                  checked={isFollowUp}
                  onChange={handleToggleFollowUp}
                />
                <div className="u-margin-left-sm u-display-flex u-alignItems-flexStart">
                  Only send this message if applicants do not respond to another {messageType}{' '}
                  message
                  <Tooltip
                    title="This message will be canceled if the applicant responds to any previously sent message for this trigger"
                    placement="right"
                  >
                    <span>
                      <MaterialDesignIcon
                        iconName={ICON_NAMES.HELP}
                        className={messageType ? '' : 'u-margin-left-lg'}
                        size="sm"
                      />
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}
            {automation?.isConfirmable ||
            communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTERVIEW_REMINDER ? (
              <div className="u-display-flex u-alignItems-flexStart u-margin-y">
                <Checkbox
                  className="u-no-padding"
                  checked={isConfirmationRequired}
                  onChange={handleToggleIsConfirmationRequired}
                />
                <div className="u-margin-left-sm">
                  Ask for confirmation (This will add a prompt to the end of your message: &quot;
                  {ConfirmableMessageText}&quot;)
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <TextField
          className="u-width-100 u-margin-y"
          variant="outlined"
          margin="dense"
          label="Title"
          value={description}
          onChange={handleDescriptionChange}
          required
          data-cy="automation-title"
        />
        <FeatureFlagged
          variant={FeatureGatedVariant.HIDE_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <LegacyMultiLanguageMessageComposer
            onChange={handleMessageChange}
            onMessageTypeChange={handleMessageTypeChange}
            onLoading={handleMessageLoading}
            enableEmail
            enableText
            canPersonalize
            canTranslate
            initialMessage={templateString}
            initialType={messageDeliveryType}
            recipientType="applicant"
            disableSubject
          />
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <MultiLanguageMessageComposer
            onChange={handleMessageChange}
            onMessageTypeChange={handleMessageTypeChange}
            onLoading={handleMessageLoading}
            enableEmail
            enableText
            canPersonalize
            canTranslate
            initialMessage={templateString}
            initialType={messageDeliveryType}
            recipientType="applicant"
            isOnMobile={isOnMobile}
            disableSubject
          />
        </FeatureFlagged>
        {templateString && (
          <div>
            <div className="typo-h6 u-margin-top-lg">Preview:</div>
            <FeatureFlagged
              variant={FeatureGatedVariant.HIDE_ON_FLAG}
              flagName={FeatureGates.INTEGRATED_MESSAGING}
            >
              <LegacyMessageTemplatePreview
                message={messageForPreview}
                parser={parser}
                isAutomated
                onError={onPreviewError}
                onChange={onTemplateChange}
                messageType={messageDeliveryType}
                templateType="message"
                sender="Team Engine (automatically)"
              />
            </FeatureFlagged>
            <FeatureFlagged
              variant={FeatureGatedVariant.SHOW_ON_FLAG}
              flagName={FeatureGates.INTEGRATED_MESSAGING}
            >
              <MessageTemplatePreview
                message={messageForPreview}
                parser={parser}
                onError={onPreviewError}
                onChange={onTemplateChange}
                messageType={messageDeliveryType}
                templateType="message"
                sender="Team Engine (automatically)"
              />
            </FeatureFlagged>
          </div>
        )}
        {showBulkUpdate && hasJobOverrides && (
          <div className="u-display-flex u-alignItems-flexStart">
            <Checkbox
              className="u-no-padding"
              checked={willUpdateAllJobs}
              onChange={handleWillUpdateAllJobsChange}
            />
            <div className="u-margin-left-sm">
              Use this updated default for all {orgName ? `${orgName} ` : ''}jobs
              <span
                role="presentation"
                className="typo-link typo-italic"
                onClick={handleToggleJobOverrideDialog}
              >
                View current custom messages
              </span>
            </div>
            {jobOverrideDialogOpen ? (
              <JobOverridesDialog
                orgId={orgId}
                orgName={orgName}
                jobOverrides={jobOverrides}
                handleToggleDialog={handleToggleJobOverrideDialog}
              />
            ) : null}
          </div>
        )}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onClose} className="u-margin-right">
          Cancel
        </UiButton>
        <UiButton disabled={disableSave} onClick={submit} dataCy="save">
          {editingForJob ? 'Save for job' : 'Save default'}
        </UiButton>
      </UiDialog.Actions>
    </UiDialog>
  );
};
